"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import styles from "./listing-image-grid.module.css";
import {
  IoImagesOutline,
  IoPlayCircleOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import { getMedia, MediaItem } from "@/lib/media/shared";
import { ListingTypeEnum } from "@/types/types";
import PhotoPreviewDialog from "./photo-preview-dialog";

interface ListingImageGridProps {
  photos: Record<string, MediaItem>;
  listingType: ListingTypeEnum;
  videoData?: MediaItem;
  labels?: string[];
}

export default function ListingImageGrid({
  photos,
  listingType,
  videoData,
  labels = [],
}: ListingImageGridProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [startSlideIndex, setStartSlideIndex] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);

  const rawPhotos = Object.values(photos || {});
  const totalPhotos = rawPhotos.length;

  const hasVideo = listingType !== ListingTypeEnum.ESSENTIAL && !!videoData;
  const videoIndex = totalPhotos;

  if (totalPhotos === 0) return null;

  const displayPhotos = rawPhotos.map((media, index) => {
    const size = index === 0 ? "lg" : "md";
    return {
      mediaId: media.MediaId || `fallback-${index}`,
      resolved: getMedia(media, size),
    };
  });

  const openGallery = (index: number) => {
    setStartSlideIndex(index);
    setIsDialogOpen(true);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -window.innerWidth * 0.9,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: window.innerWidth * 0.9,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <div className={styles.carouselWrapper}>
        {/* --- FIXED GLOBAL OVERLAYS --- */}
        {labels.length > 0 && (
          <div className={styles.labelsWrapper}>
            {labels.slice(0, 2).map((label, idx) => (
              <span key={idx} className={styles.badgeLabel}>
                {label}
              </span>
            ))}
          </div>
        )}

        {hasVideo && (
          <button
            className={styles.videoOverlayButton}
            onClick={(e) => {
              e.stopPropagation();
              openGallery(videoIndex);
            }}
          >
            <IoPlayCircleOutline className={styles.videoIcon} />
            <span>Tur video</span>
          </button>
        )}

        {/* --- MOBILE NAVIGATION ARROWS --- */}
        <button
          className={`${styles.mobileNavBtn} ${styles.navLeft}`}
          onClick={scrollLeft}
          aria-label="Previous image"
        >
          <IoChevronBack className={styles.navIcon} />
        </button>

        <button
          className={`${styles.mobileNavBtn} ${styles.navRight}`}
          onClick={scrollRight}
          aria-label="Next image"
        >
          <IoChevronForward className={styles.navIcon} />
        </button>

        {/* --- GRID / SWIPER TRACK --- */}
        <div className={styles.gridContainer} ref={scrollRef}>
          {displayPhotos.map((item, index) => {
            const isMain = index === 0;
            const isTabletLast = index === 2;
            const isDesktopLast = index === 4;

            return (
              <div
                key={item.mediaId}
                className={`${styles.imageWrapper} ${isMain ? styles.mainImage : ""}`}
                onClick={() => openGallery(index)}
              >
                {item.resolved?.src ? (
                  <Image
                    src={item.resolved.src}
                    alt={`Property view ${index + 1}`}
                    fill
                    priority={isMain}
                    sizes={isMain ? "(max-width: 1024px) 100vw, 50vw" : "25vw"}
                    className={styles.image}
                  />
                ) : (
                  <div className={styles.placeholder} />
                )}

                {/* Tablet Overlay (Displays ONLY on screens 768px - 1240px) */}
                {isTabletLast && totalPhotos > 3 && (
                  <button
                    className={`${styles.viewAllOverlay} ${styles.tabletOnlyOverlay}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery(0);
                    }}
                  >
                    <IoImagesOutline className={styles.overlayIcon} />
                    <span>Vezi toate fotografiile ({totalPhotos})</span>
                  </button>
                )}

                {/* Desktop Overlay (Displays ONLY on screens > 1240px) */}
                {isDesktopLast && totalPhotos > 5 && (
                  <button
                    className={`${styles.viewAllOverlay} ${styles.desktopOnlyOverlay}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery(0);
                    }}
                  >
                    <IoImagesOutline className={styles.overlayIcon} />
                    <span>Vezi toate fotografiile ({totalPhotos})</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <PhotoPreviewDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        photos={photos}
        video={hasVideo ? videoData : undefined}
        initialIndex={startSlideIndex}
      />
    </>
  );
}
